import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, LinearProgress, Fade, Alert, Button, Skeleton } from '@mui/material';
import { useTenantTheme } from '../tenant/TenantThemeProvider';
import { useToast } from '../ui/ToastProvider';
import { logger } from '../utils/logger';
// Generador de IDs únicos para las sesiones de análisis
import { v4 as uuidv4 } from 'uuid';

// Definir variables de configuración usando SOLO variables de entorno
const FLOWSURE_QA_BASE_URL = 'https://api-qa.flowsure.cl';
const CONFIG = {
  API_USERNAME: process.env.REACT_APP_API_USERNAME,
  API_PASSWORD: process.env.REACT_APP_API_PASSWORD,
  API_BASE_URL: FLOWSURE_QA_BASE_URL
};

const isTransientNetworkError = (error) => {
  const code = error?.code || '';
  const message = (error?.message || '').toLowerCase();
  return code === 'ECONNRESET' || code === 'ETIMEDOUT' || message.includes('socket hang up');
};

const postWithRetry = async (requestConfig, retries = 2, delayMs = 800) => {
  let attempt = 0;

  while (attempt <= retries) {
    try {
      return await axios(requestConfig);
    } catch (error) {
      if (attempt === retries || !isTransientNetworkError(error)) {
        throw error;
      }

      const waitMs = delayMs * (attempt + 1);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      attempt += 1;
    }
  }

  throw new Error('No se pudo completar la solicitud tras reintentos.');
};

// logger.debug('Configuración cargada desde variables de entorno:', {
//   API_USERNAME: process.env.REACT_APP_API_USERNAME ? 'Definido' : 'No definido',
//   API_PASSWORD: process.env.REACT_APP_API_PASSWORD ? 'Definido' : 'No definido',
//   API_BASE_URL: process.env.REACT_APP_API_BASE_URL
// });

const ProcessImages = ({ setResultadosAnalisis, totalFotosRequeridas }) => {
  const history = useHistory();
  const { brandColors, tenantConfig } = useTenantTheme();
  const { showError, showInfo, showSuccess } = useToast();
  const [processingStatus, setProcessingStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [ubicacion, setUbicacion] = useState(null);
  const [processError, setProcessError] = useState('');
  const [retryCounter, setRetryCounter] = useState(0);
  const sessionIdRef = useRef(null);

  const getAuthToken = useCallback(async () => {
    try {
      // Usar variables de configuración
      const username = CONFIG.API_USERNAME;
      const password = CONFIG.API_PASSWORD;
      const apiBaseUrl = CONFIG.API_BASE_URL;
      
      // logger.debug('Variables para autenticación:', { 
      //   username: username, 
      //   password: password ? 'Definido' : 'No definido',
      //   apiBaseUrl
      // });
      
      if (!username || !password) {
        throw new Error('Faltan credenciales FlowSure (REACT_APP_API_USERNAME / REACT_APP_API_PASSWORD).');
      }

      logger.debug(`Intentando autenticar con ${apiBaseUrl}/auth/token`);
      const response = await axios.post(`${apiBaseUrl}/auth/token`, {
        username,
        password
      });
      logger.debug('Token obtenido correctamente');
      return response.data.access_token;
    } catch (error) {
      logger.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  }, []);

  // Verificar variables de entorno al inicio del componente
  useEffect(() => {
    logger.debug('Usando configuración:');
    // logger.debug('API_USERNAME:', CONFIG.API_USERNAME ? 'Definido' : 'No definido');
    // logger.debug('API_PASSWORD:', CONFIG.API_PASSWORD ? 'Definido' : 'No definido');
    logger.debug('API_BASE_URL:', CONFIG.API_BASE_URL);
  }, []);

  // Obtener la ubicación al cargar el componente
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          const timestamp = position.timestamp;
          setUbicacion({
            latitud: latitude,
            longitud: longitude,
            precision: accuracy,
            timestamp: timestamp,
            fecha: new Date(timestamp).toLocaleString()
          });
          // logger.debug('Ubicación obtenida:', { latitude, longitude, accuracy });
        },
        (error) => {
          logger.error('Error al obtener la ubicación:', error.message);
        },
        { enableHighAccuracy: true }
      );
    } else {
      logger.error('Geolocalización no soportada por este navegador');
    }
  }, []);

  // Función para formatear la patente
  const formatearPatente = (patente) => {
    let patenteFormateada = patente.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (patenteFormateada.length > 6) {
      patenteFormateada = patenteFormateada.slice(0, 6);
    } else if (patenteFormateada.length < 6) {
      patenteFormateada = patenteFormateada.padEnd(6, 'X');
    }
    return patenteFormateada.slice(0, 4) + patenteFormateada.slice(4);
  };

  // Función para obtener o generar un ID de sesión único
  const getSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `vehicle-inspection-${uuidv4()}`;
      logger.debug('Nuevo ID de sesión generado para esta corrida:', sessionIdRef.current);
    }
    return sessionIdRef.current;
  }, []);

  const procesarDocumento = useCallback(async (image) => {
    try {
      logger.debug('Enviando foto a la API de documentos');
      const token = await getAuthToken();
      
      const apiBaseUrl = CONFIG.API_BASE_URL;
      const sessionId = getSessionId();
      
      // Esquema de extracción personalizado según los requisitos
      const extractionSchema = {
      "Marca": {
          "type": "string",
          "description": "Marca del fabricante del vehículo."
      },
      "Modelo": {
          "type": "string",
          "description": "Modelo del vehículo."
      },
      "Patente": {
          "type": "string",
          "description": "Inscripción, patente, código PPU y placa en Chile. Ejemplo: 'RZZR.43-7', 'ABCD45'."
      },
      "Año": {
          "type": "integer",
          "description": "Año de fabricación o registro del vehículo."
      },
      "Numero_chasis": {
          "type": "string",
          "description": "Número único que identifica el chasis del vehículo (VIN). Suele contener exactamente 17 caracteres alfanuméricos sin espacios ni caracteres especiales. En documentos chilenos, aparece como 'VIN' o 'Nº VIN' o bajo 'Nro. CHASIS' o 'Nro. SERIE'. Ejemplo: 'ABOCFSICA NM184205'."
      },
      "Rut_adquiriente": {
          "type": "string",
          "description": "RUT del adquiriente del vehículo. Formato: 8 dígitos seguidos de un guion y un dígito verificador o 'K'. Ejemplo: 11222333-K."
      }
      };
      
      const response = await postWithRetry({
        method: "POST",
        url: `${apiBaseUrl}/extractor_ia/`,
        data: {
          image_base64: image.split(',')[1],
          id_grupo_cliente: sessionId,
          extraction_schema: extractionSchema
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        timeout: 120000,
        maxBodyLength: Infinity
      });
      
      logger.debug('Respuesta completa de la API de documentos:', response.data);
      
      // Mapeamos la nueva estructura de respuesta
      return {
        result: {
          atencion_id: response.data.atencion_id,
          extracted_data: response.data.extracted_data || {},
          atencion_info: response.data.atencion_info || {},
          image_quality: response.data.image_quality || {}
        }
      };
    } catch (error) {
      logger.error('Error al procesar documento:', error);
      throw error;
    }
  }, [getAuthToken, getSessionId]);

  const procesarDanos = useCallback(async (image, photoIndex) => {
    try {
      logger.debug('Enviando foto a la API de daños');
      const token = await getAuthToken();
      
      const apiBaseUrl = CONFIG.API_BASE_URL;
      const sessionId = getSessionId();
      
      const response = await postWithRetry({
        method: "POST",
        url: `${apiBaseUrl}/extractor_damage/`,
        data: {
          image_base64: image.split(',')[1],
          id_grupo_cliente: sessionId
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        timeout: 120000,
        maxBodyLength: Infinity
      });
      
      logger.debug('Respuesta de la API de detección de daños:', response.data);
      
      // Asegurar que la estructura de la respuesta sea coherente
      if (response.data && response.data.result) {
        // Verificar si visualization es una URL o una cadena base64
        if (response.data.result.visualization && typeof response.data.result.visualization === 'string') {
          // Si comienza con '/' o 'http', es una URL y no necesitamos formatearla
          if (response.data.result.visualization.startsWith('/') || 
              response.data.result.visualization.startsWith('http')) {
            logger.debug('La visualización es una URL y debe procesarse:', 
              response.data.result.visualization.substring(0, 30) + '...');
          }
        }
        
        // Asegurarnos de que tenemos la estructura detection_result
        if (!response.data.result.detection_result && (
            response.data.result.all_damages || 
            response.data.result.part_damages ||
            response.data.result.ocr_data)) {
          // La estructura ya está plana, convertimos a detection_result para mantener coherencia
          response.data.result.detection_result = {
            predictions: response.data.result.vehicle_identifiers || [],
            part_damages: response.data.result.part_damages || [],
            all_damages: response.data.result.all_damages || [],
            ocr_data: response.data.result.ocr_data || {},
            image_dimensions: response.data.result.image_dimensions || {},
            visualization: response.data.result.visualization || null
          };
        }
      }
      
      // Devolvemos la respuesta completa de la API
      return response.data;
    } catch (error) {
      logger.error('Error al procesar daños:', error);
      throw error;
    }
  }, [getAuthToken, getSessionId]);

  const procesarOdometro = useCallback(async (image) => {
    try {
      logger.debug('Procesando imagen del odómetro...');
      
      // Obtener token de autenticación
      const token = await getAuthToken();
      
      // Usar variables de configuración de la nueva API
      const apiBaseUrl = CONFIG.API_BASE_URL;
      
      const sessionId = getSessionId();
      logger.debug('Preparando datos para nueva API de odómetro');
      
      // Convertir la imagen base64 a formato adecuado
      const imageBase64 = image.split(',')[1]; // Eliminar el prefijo 'data:image/jpeg;base64,'
      
      logger.debug('Enviando solicitud a nueva API para odómetro...');
      const response = await postWithRetry({
        method: "POST",
        url: `${apiBaseUrl}/extractor_dashboard/`,
        data: {
          image_base64: imageBase64,
          id_grupo_cliente: sessionId
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        timeout: 120000,
        maxBodyLength: Infinity
      });
      
      logger.debug('Respuesta de la nueva API de odómetro:', response.data);
      
      // Verificar que la respuesta tenga la estructura esperada
      if (response.data && response.data.result) {
        const result = response.data.result;
        
        // Extraer el texto del odómetro desde ocr_data
        let odometroText = '';
        if (result.ocr_data && result.ocr_data.odometer) {
          odometroText = result.ocr_data.odometer;
          logger.debug('Lectura del odómetro detectada:', odometroText);
        }
        
        // Extraer la visualización desde detection_result
         let visualization = null;
         if (result.detection_result && result.detection_result.visualization) {
           // La visualización viene en base64, agregar el prefijo
           visualization = `data:image/jpeg;base64,${result.detection_result.visualization}`;
           logger.debug('Visualización del odómetro detectada');
         } else {
           logger.debug('No se encontró visualización en la respuesta del odómetro, usando imagen original');
         }
         
         // Crear objeto de resultado
         const testigos = [];
         const detectionResult = result.detection_result || {};
         ['check_engine', 'airbag'].forEach((indicatorKey) => {
          const entries = Array.isArray(detectionResult[indicatorKey]) ? detectionResult[indicatorKey] : [];
          entries.forEach((entry) => {
            testigos.push({
              class: entry.class || indicatorKey,
              confidence: entry.confidence || 0,
              estado: 'activo',
              class_id: entry.class_id
            });
          });
         });

         return {
           texto: odometroText,
            visualization: visualization || image, // Usar la visualización detectada o la original
            testigos,
            odometro_detectado: result.odometer_detected || (odometroText ? true : false)
          };
      }
      
      logger.warn('Estructura de respuesta inesperada en nueva API de odómetro');
      return null;
    } catch (error) {
      logger.error('Error al procesar odómetro:', error);
      logger.error('Detalle del error:', error.response ? error.response.data : error.message);
      return null;
    }
  }, [getAuthToken, getSessionId]);

  const procesarChasis = useCallback(async (image) => {
    try {
      logger.debug('Procesando imagen del chasis...');
      
      // Obtener token de autenticación
      const token = await getAuthToken();
      
      // Usar variables de configuración de la nueva API
      const apiBaseUrl = CONFIG.API_BASE_URL;
      
      const sessionId = getSessionId();
      logger.debug('Preparando datos para nueva API de chasis');
      
      // Convertir la imagen base64 a formato adecuado
      const imageBase64 = image.split(',')[1]; // Eliminar el prefijo 'data:image/jpeg;base64,'
      
      logger.debug('Enviando solicitud a nueva API para chasis...');
      const response = await postWithRetry({
        method: "POST",
        url: `${apiBaseUrl}/extractor_chassis/`,
        data: {
          image_base64: imageBase64,
          id_grupo_cliente: sessionId
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        timeout: 120000,
        maxBodyLength: Infinity
      });
      
      logger.debug('Respuesta de la nueva API de chasis:', response.data);
      
      // Verificar que la respuesta tenga la estructura esperada
      if (response.data && response.data.result) {
        const result = response.data.result;
        
        // Extraer el texto del chasis desde ocr_data
        let chasisText = '';
        if (result.ocr_data && result.ocr_data.chassis) {
          chasisText = result.ocr_data.chassis;
          logger.debug('Número de chasis detectado:', chasisText);
        }
        
        // Extraer la visualización desde detection_result
         let visualization = null;
         if (result.detection_result && result.detection_result.visualization) {
           // La visualización viene en base64, agregar el prefijo
           visualization = `data:image/jpeg;base64,${result.detection_result.visualization}`;
           logger.debug('Visualización del chasis detectada');
         } else {
           logger.debug('No se encontró visualización en la respuesta del chasis, usando imagen original');
         }
         
         // Crear objeto de resultado
         return {
           texto: chasisText,
           visualization: visualization || image, // Usar la visualización detectada o la original
           chasis_detectado: result.chassis_detected || (chasisText ? true : false)
         };
      }
      
      logger.warn('Estructura de respuesta inesperada en nueva API de chasis');
      return null;
    } catch (error) {
      logger.error('Error al procesar chasis:', error);
      logger.error('Detalle del error:', error.response ? error.response.data : error.message);
      return null;
    }
  }, [getAuthToken, getSessionId]);

  const guardarEnLocalStorage = useCallback((resultados) => {
    try {
      // Crear una versión optimizada de los resultados para localStorage
      const resultadosOptimizados = {
        danos: [],
        odometro: null,
        patentes: [],  // Inicializar como array vacio
        chasis: null,
        documento: null,
        marcas: null,
        ubicacion: ubicacion, // Ubicación general del dispositivo
        ubicaciones: {} // Ubicaciones específicas de cada foto
      };

      // Solo guardar IDs e información esencial para daños, no imágenes completas
      if (resultados.danos && resultados.danos.length) {
        resultadosOptimizados.danos = resultados.danos.map(dano => ({
          indice: dano.indice,
          // No guardar visualizaciones grandes en localStorage
          danos: dano.danos ? dano.danos.map(d => ({
            class: d.class,
            confidence: d.confidence,
            indice: d.indice
          })) : []
        }));
      }

      // Solo guardar información esencial para patentes
      if (resultados.patentes && resultados.patentes.length) {
        resultadosOptimizados.patentes = resultados.patentes.map(patente => ({
          indice: patente.indice,
          // No guardar visualizaciones
          datos: patente.datos ? {
            extracted_text: patente.datos.extracted_text
          } : {}
        }));
      }

      // Solo guardar datos del documento, no la imagen completa
      if (resultados.documento) {
        resultadosOptimizados.documento = {
          // No guardar imagen completa
          datos: resultados.documento.datos || {}
        };
      }

      // Verificar tamaño aproximado antes de guardar
      const jsonSize = JSON.stringify(resultadosOptimizados).length;
      logger.debug(`Tamaño aproximado de los datos a guardar: ${jsonSize} bytes`);
      
      // Si el tamaño es muy grande, reducir aún más
      if (jsonSize > 4000000) { // 4MB límite aproximado
        logger.warn('Datos muy grandes para localStorage, reduciendo aún más');
        // Solo guardar ids y conteo
        resultadosOptimizados.danos = resultadosOptimizados.danos.map(dano => ({
          indice: dano.indice,
          danos_count: dano.danos ? dano.danos.length : 0
        }));
      }

      localStorage.setItem('resultadosResumen', JSON.stringify(resultadosOptimizados));
      logger.debug('Resumen de resultados guardado en localStorage con éxito');
    } catch (error) {
      logger.error('Error al guardar resultados en localStorage:', error);
      // Intentar limpiar localStorage si hay un error de cuota
      if (error.name === 'QuotaExceededError') {
        try {
          localStorage.removeItem('imagenesProcessadas');
          localStorage.removeItem('resultadosResumen');
          logger.debug('localStorage limpiado por exceso de cuota');
        } catch (e) {
          logger.error('No se pudo limpiar localStorage:', e);
        }
      }
    }
  }, [ubicacion]);

  useEffect(() => {
    const processImages = async () => {
      setProcessError('');
      sessionIdRef.current = `vehicle-inspection-${uuidv4()}`;
      logger.debug('Iniciando procesamiento con sessionId:', sessionIdRef.current);

      const resultados = {
        danos: [],
        odometro: null,
        patentes: [],  // Inicializar como array vacio
        chasis: null,
        documento: null,
        marcas: null,
        ubicacion: ubicacion, // Ubicación general del dispositivo
        ubicaciones: {} // Ubicaciones específicas de cada foto
      };

      const totalSteps = 5;
      let currentStep = 0;

      const updateProgress = (step, message) => {
        currentStep = step;
        setProgress((currentStep / totalSteps) * 100);
        setProcessingStatus(message);
      };
      
      updateProgress(1, 'Iniciando análisis de imágenes...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      updateProgress(2, 'Analizando fotos exteriores...');
      // Procesar solo las 4 fotos exteriores
      for (let i = 0; i < 4; i++) { // 0: frontal, 1: trasera, 2: izquierda, 3: derecha
        const photoData = localStorage.getItem(`photo_${i}`);
        
        // Obtener datos de ubicación de la foto
        try {
          const ubicacionData = localStorage.getItem(`photo_${i}_ubicacion`);
          if (ubicacionData) {
            resultados.ubicaciones[i] = JSON.parse(ubicacionData);
              // logger.debug(`Ubicación recuperada para foto ${i}:`, resultados.ubicaciones[i]);
          }
        } catch (error) {
          logger.error(`Error al procesar la ubicación de la foto ${i}:`, error);
        }
        
        if (photoData) {
          try {
            // Procesar daños
            const responseDanos = await procesarDanos(photoData);
            if (responseDanos) {
              // Determinar estructura de la respuesta
              const useDetectionResult = responseDanos.result && (responseDanos.result.detection_result !== undefined);
              logger.debug(`Foto ${i} - Usando estructura detection_result:`, useDetectionResult);
              
              // Obtener la visualización ya procesada por procesarDanos
              let visualizacionStr = null;
              
              if (useDetectionResult) {
                // Estructura con detection_result
                const visualization = responseDanos.result.detection_result?.visualization;
                if (visualization) {
                  if (visualization.startsWith('/') || visualization.startsWith('http')) {
                    // Es una URL, usamos directamente
                    visualizacionStr = visualization;
                    logger.debug(`Visualización URL para foto ${i}:`, visualizacionStr.substring(0, 30) + '...');
                  } else {
                    // Es base64
                    visualizacionStr = `data:image/jpeg;base64,${visualization}`;
                    logger.debug(`Visualización base64 para foto ${i} lista para mostrar`);
                  }
                }
              } else {
                // Estructura plana
                const visualization = responseDanos.result.visualization;
                if (visualization) {
                  if (visualization.startsWith('/') || visualization.startsWith('http')) {
                    visualizacionStr = visualization;
                    logger.debug(`Visualización URL para foto ${i}:`, visualizacionStr.substring(0, 30) + '...');
                  } else {
                    visualizacionStr = `data:image/jpeg;base64,${visualization}`;
                    logger.debug(`Visualización base64 para foto ${i} lista para mostrar`);
                  }
                }
              }
              
              if (!visualizacionStr) {
                logger.debug(`No se encontró visualización para foto ${i}`);
              }
              
              // Extraer datos importantes dependiendo de la estructura
              const damageAllDamages = useDetectionResult ? 
                responseDanos.result.detection_result?.all_damages : 
                responseDanos.result.all_damages;
              
              const damagePartDamages = useDetectionResult ? 
                responseDanos.result.detection_result?.part_damages : 
                responseDanos.result.part_damages;
              
              const damageOcrData = useDetectionResult ? 
                responseDanos.result.detection_result?.ocr_data : 
                responseDanos.result.ocr_data;
              
              const damageLicensePlate = damageOcrData?.license_plate;
              
              logger.debug(`Foto ${i} - All damages:`, damageAllDamages);
              logger.debug(`Foto ${i} - Part damages:`, damagePartDamages);
              logger.debug(`Foto ${i} - License plate:`, damageLicensePlate);
              
              // Agregar el índice de la imagen a la respuesta completa para identificar a qué foto corresponde
              responseDanos.indice = i;
              
              // Extraer predicciones de daños como identificadores de vehículo
              const predicciones = useDetectionResult ? 
                responseDanos.result.detection_result?.predictions ||
                responseDanos.result.detection_result?.vehicle_identifiers ||
                [] : 
                responseDanos.result.vehicle_identifiers || [];
              
              // Separar marcas de daños
              const marcas = [];
              const prediccionesFiltradas = predicciones.filter(pred => {
                // Comprobar si es una marca de vehículo (termina en -marca)
                if (pred.class.toLowerCase().endsWith('-marca')) {
                  // Extraer nombre de marca (eliminar el sufijo -marca)
                  marcas.push({
                    ...pred,
                    nombreMarca: pred.class.split('-')[0]
                  });
                  return false; // No incluir en predicciones de daños
                }
                
                // Filtrar solo patentes
                return !pred.class.toLowerCase().includes('patente');
              });
              
              // Guardar marcas detectadas en los resultados
              if (marcas.length > 0) {
                // Extraer solo el nombre de la marca como string
                const nombreMarca = marcas[0].nombreMarca || marcas[0].class.split('-')[0] || 'Desconocida';
                
                // Guardar la marca y asegurarse de que se actualice incluso si ya existe una (para fotos traseras)
                resultados.marcas = nombreMarca; // Guardar solo el string con el nombre de la marca
                logger.debug(`Marca detectada en foto ${i} y guardada:`, nombreMarca);
                
                // Agregar marca al objeto de daños para asegurar que se procese también desde la foto trasera
                responseDanos.marca = nombreMarca;
              }
              
              // Buscar predicciones de patente (solo para fotos frontal y trasera)
              if (i === 0 || i === 1) {
                const patentePrediction = predicciones.find(p => 
                  p.class.toLowerCase().includes('patente'));
                
                if (patentePrediction) {
                  // Extraer texto de patente si existe
                  let textoPatente = '';
                  
                  // Intentar obtener el texto de la patente desde ocr_data
                  if (damageOcrData && damageOcrData.license_plate) {
                    textoPatente = damageOcrData.license_plate;
                    logger.debug(`Patente encontrada en OCR data: ${textoPatente}`);
                  } else if (patentePrediction.extracted_text) {
                    textoPatente = patentePrediction.extracted_text;
                    logger.debug(`Patente encontrada en predicción: ${textoPatente}`);
                  }
                  
                  logger.debug(`Texto de patente extraído para foto ${i}:`, textoPatente);
                  
                  // Formatear y guardar la patente
                  const patenteFormateada = formatearPatente(textoPatente);
                  logger.debug(`Patente formateada para foto ${i}:`, patenteFormateada);
                  
                  resultados.patentes.push({
                    indice: i,
                    visualizacion: visualizacionStr,
                    datos: {
                      extracted_text: patenteFormateada
                    },
                    prediccion: patentePrediction
                  });
                }
              }
              
              // Usar los datos ya extraidos anteriormente
              const vehicleIdentifiers = useDetectionResult ? 
                  responseDanos.result.detection_result?.predictions ||
                  responseDanos.result.detection_result?.vehicle_identifiers || [] : 
                  responseDanos.result.vehicle_identifiers || [];
              
              // Guardar toda la respuesta original de la API más los datos procesados
              resultados.danos.push({
                indice: i,
                visualizacion: visualizacionStr, // Ya está formateado correctamente
                danos: prediccionesFiltradas.map(danio => ({ ...danio, indice: i })),
                all_damages: damageAllDamages || [],
                part_damages: damagePartDamages || [],
                vehicle_identifiers: vehicleIdentifiers || [],
                ocr_data: damageOcrData || {},
                license_plate: damageLicensePlate || null,
                apiResponse: responseDanos // Guardar la respuesta completa para acceder a ella después
              });
            }
          } catch (error) {
            logger.error(`Error al analizar la foto ${i}:`, error);
          }
        }
      }

      updateProgress(3, 'Análisis de fotos exteriores completado');
      
      // Procesamiento del odómetro
      updateProgress(4, 'Procesando odómetro...');
      const odometroImage = localStorage.getItem('photo_7');
      
      // Obtener ubicación del odómetro
      try {
        const ubicacionOdometro = localStorage.getItem('photo_7_ubicacion');
        if (ubicacionOdometro) {
          resultados.ubicaciones[7] = JSON.parse(ubicacionOdometro);
          // logger.debug('Ubicación recuperada para odómetro:', resultados.ubicaciones[7]);
        }
      } catch (error) {
        logger.error('Error al procesar la ubicación del odómetro:', error);
      }
      
      logger.debug('Verificando imagen de odómetro en localStorage:', {
        existe: !!odometroImage,
        tamano: odometroImage ? odometroImage.length : 0,
        inicio: odometroImage ? odometroImage.substring(0, 50) + '...' : 'N/A'
      });
      
      if (odometroImage) {
        try {
          const odometroResultado = await procesarOdometro(odometroImage);
          logger.debug('Procesando imagen tipo odómetro, respuesta completa:', odometroResultado);
          
          if (odometroResultado) {
            // Guardar todos los datos del odómetro
            logger.debug('Guardando resultado completo del odómetro:', odometroResultado);
            
            // Crear un objeto estructurado para el odómetro que sea compatible con AnalisisGeneral.js
            resultados.odometro = {
              texto: odometroResultado.texto,
              testigos: odometroResultado.testigos || [],
              visualization: null // Se ajustará a continuación
            };
            
            // Para compatibilidad con código existente
            resultados.indicadores = odometroResultado.indicadores;
            
            // Manejar la visualización del odómetro - asegurar formato correcto
            if (odometroResultado.visualization) {
              // Asegurar que sea string y tenga el formato correcto para su uso en <img>
              if (typeof odometroResultado.visualization === 'string') {
                let visualizationStr = odometroResultado.visualization;
                if (!visualizationStr.startsWith('data:') && 
                    !visualizationStr.startsWith('http')) {
                  visualizationStr = `data:image/jpeg;base64,${visualizationStr}`;
                }
                // Guardar la visualización en el objeto odometro
                resultados.odometro.visualization = visualizationStr;
                logger.debug('Visualización del odómetro formateada correctamente:', 
                          visualizationStr.substring(0, 50) + '...');
              } else {
                logger.warn('La visualización del odómetro no es un string válido:', typeof odometroResultado.visualization);
              }
            } else {
              logger.warn('No se encontró visualización de odómetro en la respuesta');
            }
            
            // Manejar los testigos
            if (odometroResultado.testigos && Array.isArray(odometroResultado.testigos)) {
              resultados.testigos = odometroResultado.testigos.map(testigo => ({
                class: testigo.class || testigo.tipo || 'Testigo desconocido',
                confidence: testigo.confidence || testigo.confianza || 0.5,
                x: testigo.x || 0,
                y: testigo.y || 0,
                width: testigo.width || testigo.ancho || 50,
                height: testigo.height || testigo.alto || 50
              }));
              logger.debug(`${resultados.testigos.length} testigos guardados:`, 
                        resultados.testigos.map(t => t.class).join(', '));
            } else {
              logger.warn('No se encontraron testigos válidos en la respuesta del odómetro');
              resultados.testigos = [];
            }
          }
        } catch (error) {
          logger.error("Error al procesar la foto del odómetro:", error);
        }
      } else {
        logger.warn('No se encontró imagen de odómetro para procesar');
      }
      
      // Procesamiento del documento
      updateProgress(4, 'Procesando documento...');
      const documentoImage = localStorage.getItem('photo_10');
      
      // Obtener ubicación del documento
      try {
        const ubicacionDocumento = localStorage.getItem('photo_10_ubicacion');
        if (ubicacionDocumento) {
          resultados.ubicaciones[10] = JSON.parse(ubicacionDocumento);
          // logger.debug('Ubicación recuperada para documento:', resultados.ubicaciones[10]);
        }
      } catch (error) {
        logger.error('Error al procesar la ubicación del documento:', error);
      }
      
      if (documentoImage) {
        try {
          const documentoResultado = await procesarDocumento(documentoImage);

          if (documentoResultado && documentoResultado.result) {
            resultados.documento = {
              imagen: documentoImage, // Mantener la imagen original para mostrarla
              datos: documentoResultado.result || {}
            };
            const extractedData = documentoResultado.result.extracted_data || {};
            logger.debug('Datos de documento procesados. Campos extraídos:', Object.keys(extractedData));
          } else {
            resultados.documento = {
              imagen: documentoImage,
              datos: {}
            };
            logger.debug('No se obtuvieron datos del documento en la estructura esperada');
          }
        } catch (error) {
          logger.error("Error al procesar la foto del documento:", error);
          resultados.documento = {
            imagen: documentoImage,
            datos: {}
          };
        }
      } else {
        logger.warn('No se encontró imagen de documento para procesar');
      }
      
      // Procesamiento del chasis
      updateProgress(4, 'Procesando chasis...');
      // La imagen del chasis/VIN debe ser la foto 9, no la 11
      const chasisImage = localStorage.getItem('photo_9');
      
      // Obtener ubicación del chasis
      try {
        const ubicacionChasis = localStorage.getItem('photo_9_ubicacion');
        if (ubicacionChasis) {
          resultados.ubicaciones[9] = JSON.parse(ubicacionChasis);
          // logger.debug('Ubicación recuperada para chasis:', resultados.ubicaciones[9]);
        }
      } catch (error) {
        logger.error('Error al procesar la ubicación del chasis:', error);
      }
      
      logger.debug('Verificando imagen de chasis/VIN en localStorage:', {
        existe: !!chasisImage,
        tamano: chasisImage ? chasisImage.length : 0,
        inicio: chasisImage ? chasisImage.substring(0, 50) + '...' : 'N/A'
      });
      
      if (chasisImage) {
        try {
          const chasisResultado = await procesarChasis(chasisImage);

          if (chasisResultado) {
            logger.debug('Texto del chasis detectado:', chasisResultado.texto);
            logger.debug('Chasis detectado?', chasisResultado.chasis_detectado);
            resultados.chasis = chasisResultado;
          } else {
            logger.warn('No se obtuvo resultado de chasis para esta imagen.');
          }
        } catch (error) {
          logger.error("Error al procesar la foto del chasis:", error);
        }
      } else {
        logger.warn('No se encontró imagen de chasis para procesar');
      }
      
      updateProgress(5, 'Análisis completado');
      logger.debug('Resultados finales:', resultados);
      logger.debug('Resultados finales antes de retornar:', JSON.stringify(resultados, null, 2));
      
      // Crear un objeto para almacenar todos los resultados
      const resultadosFinal = {
        fecha: new Date().toISOString(),
        danos: resultados.danos,
        odometro: resultados.odometro,
        chasis: resultados.chasis,
        documento: resultados.documento,
        patentes: resultados.patentes,
        patentesFrontalTrasera: {
          frontal: null,
          trasera: null
        },
        procesadoCompleto: true,
        apiResponse: resultados.danos && resultados.danos.length > 0 ? resultados.danos[0].apiResponse : null // Guardar la respuesta completa de la API
      };

      // Asegurarse de que las patentes estén correctamente formateadas antes de pasarlas
      if (resultados.patentes && resultados.patentes.length > 0) {
        logger.debug('Patentes antes de guardar:', JSON.stringify(resultados.patentes));
        
        // Guardar referencias directas a patentes frontal y trasera
        resultados.patentes.forEach(patente => {
          if (patente.indice === 0) {
            resultadosFinal.patentesFrontalTrasera.frontal = patente.datos?.extracted_text || null;
          } else if (patente.indice === 1) {
            resultadosFinal.patentesFrontalTrasera.trasera = patente.datos?.extracted_text || null;
          }
        });
        
        logger.debug('Patentes frontal/trasera:', resultados.patentesFrontalTrasera);
      } else {
        logger.debug('No hay patentes detectadas');
        // Asegurar que patentes sea un array vacío y no null/undefined
        resultados.patentes = [];
        resultados.patentesFrontalTrasera = {
          frontal: null,
          trasera: null
        };
      }
      
      // Guardar los resultados en el estado de la aplicación usando el objeto resultadosFinal
      setResultadosAnalisis(resultadosFinal);
      
      try {
        // Guardar los resultados sin incluir las imágenes originales
        const resultadosOptimizados = JSON.parse(JSON.stringify(resultadosFinal));
        
        // Eliminar las imágenes originales para ahorrar espacio
        // Pero mantener las visualizaciones que vienen de la API
        if (resultadosOptimizados.danos) {
          resultadosOptimizados.danos.forEach(item => {
            // Mantener la visualización pero eliminar la imagen original
            delete item.imagen;
            
            // Asegurarse de que la visualización esté en el formato correcto
            if (item.visualizacion && !item.visualizacion.startsWith('data:')) {
              item.visualizacion = `data:image/jpeg;base64,${item.visualizacion}`;
            }
          });

          // Depuramos el objeto apiResponse para ahorrar espacio
          if (resultadosOptimizados.apiResponse) {
            logger.debug('Guardando apiResponse en resultadosOptimizados:', resultadosOptimizados.apiResponse);
          }
        }
        
        if (resultadosOptimizados.patentes) {
          resultadosOptimizados.patentes.forEach(item => {
            delete item.imagen;
            
            // Asegurarse de que la visualización esté en el formato correcto
            if (item.visualizacion && !item.visualizacion.startsWith('data:')) {
              item.visualizacion = `data:image/jpeg;base64,${item.visualizacion}`;
            }
          });
        }
        
        // Agregar documento original a resultadosOptimizados
        if (resultadosOptimizados.documento) {
          resultadosOptimizados.documento.imagenOriginal = resultadosOptimizados.documento.imagen;
        }
        
        // Verificar específicamente los datos del chasis antes de guardar
        if (resultadosOptimizados.chasis) {
          logger.debug('Datos del chasis a guardar:', {
            texto: resultadosOptimizados.chasis.texto,
            chasis_detectado: resultadosOptimizados.chasis.chasis_detectado,
            visualization_length: resultadosOptimizados.chasis.visualization ? resultadosOptimizados.chasis.visualization.length : 0
          });
        }
        
        guardarEnLocalStorage(resultadosOptimizados);
      } catch (error) {
        logger.error('Error al guardar resultados en localStorage:', error);
        // Intenta guardar solo los datos esenciales si falla
        const datosEsenciales = {
          fecha: new Date().toISOString(),
          procesadoCompleto: true
        };
        localStorage.setItem('imagenesProcessadas', JSON.stringify(datosEsenciales));
      }
      
      history.push('/analisis-general');
      showSuccess('Análisis completado con éxito.');
    };

    processImages().catch((error) => {
      logger.error('Error general en el procesamiento de imágenes:', error);
      setProcessingStatus('No pudimos completar el análisis.');
      setProcessError('Hubo un problema al procesar las imágenes. Verifique conexión y reintente.');
      setProgress(0);
      showError('No pudimos completar el análisis. Revisa tu conexión e inténtalo nuevamente.');
    });
  }, [
    setResultadosAnalisis,
    history,
    totalFotosRequeridas,
    procesarDocumento,
    procesarDanos,
    procesarOdometro,
    procesarChasis,
    guardarEnLocalStorage,
    ubicacion,
    retryCounter,
    showError,
    showInfo,
    showSuccess
  ]);

  const handleRetry = () => {
    localStorage.removeItem('imagenesProcessadas');
    localStorage.removeItem('resultadosResumen');
    sessionIdRef.current = null;
    setProcessingStatus('');
    setProgress(0);
    setProcessError('');
    setRetryCounter((prev) => prev + 1);
    showInfo('Reintentando procesamiento de imágenes...');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: `linear-gradient(45deg, ${brandColors.primary} 0%, ${brandColors.primary} 40%, ${brandColors.secondary} 100%)`,
        padding: 3,
        color: brandColors.onPrimary
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ 
        fontWeight: 600, 
        color: brandColors.onPrimary,
        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }}>
        Procesando Imágenes - {tenantConfig.branding.name}
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ 
        marginBottom: '2rem', 
        textAlign: 'center',
        color: brandColors.onPrimary,
        opacity: 0.8
      }}>
        {processingStatus}
      </Typography>
      {processError && (
        <Box sx={{ mb: 2, width: '100%', maxWidth: '640px' }}>
          <Alert
            severity="error"
            action={(
              <Button size="small" color="inherit" onClick={handleRetry}>
                Reintentar
              </Button>
            )}
          >
            {processError}
          </Alert>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => history.push('/checklist')}
              sx={{ borderColor: brandColors.onPrimary, color: brandColors.onPrimary }}
            >
              Volver al checklist
            </Button>
          </Box>
        </Box>
      )}
      {!processError && progress < 100 && (
        <Box sx={{ width: '100%', maxWidth: '640px', mb: 2 }}>
          <Skeleton variant="rounded" height={18} sx={{ mb: 1, opacity: 0.7 }} />
          <Skeleton variant="rounded" height={12} sx={{ mb: 1, opacity: 0.5 }} />
          <Skeleton variant="rounded" height={12} sx={{ width: '70%', opacity: 0.4 }} />
        </Box>
      )}
      <Box width="80%" maxWidth="400px">
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{
            height: 10, 
            borderRadius: 5, 
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: brandColors.onPrimary
            }
          }} 
        />
      </Box>
      <Fade in={progress === 100} style={{ transitionDelay: '500ms' }}>
        <Typography variant="body1" sx={{ 
          marginTop: '2rem', 
          fontWeight: 'bold',
          color: brandColors.onPrimary
        }}>
          ¡Análisis completado!
        </Typography>
      </Fade>
    </Box>
  );
};

export default ProcessImages;
